import React from 'react';
import moment from 'moment/moment';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import type { ColumnsType } from 'antd/es/table';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { HasAccess } from 'components/common/accessControl';
import { BrandsDrawer } from 'pages/admin/purchaseOrders/brandsDrawer';
import { FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import { usePurchaseOrder } from 'pages/admin/purchaseOrders/usePurchaseOrder';
import { Button, Col, Input, PageTitle, Row, Table, Typography } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import styles from './styles.module.scss';
import { Filters } from './filters';
import { PurchaseOrderStatusBadge } from 'components/common/StatusBadge';

const { Search } = Input;
const { Text } = Typography;

export interface PurchaseOrderDataType {
  id: string;
  uuid: string | number;
  name: string;
  createDate: any;
  lastModifiedDate: any;
  deliveryDeadlineTo: any;
  deliveryDeadlineFrom: any;
  postSalesManagerAssistant: any;
}

export const PurchaseOrders: React.FC = () => {
  const { hasAccess } = useAccessControl();
  const {
    filtersForm,
    onCloseFilters,
    onOpenFilters,
    onAddNew,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    applyFilters,
    resetFilters,
    onBrandDrawerExpand,
    brandDrawerState,
    brandsData,
  } = usePurchaseOrder();
  const columns: ColumnsType<PurchaseOrderDataType> = [
    {
      title: 'ID',
      dataIndex: 'uuid',
      key: 'uuid',
    },
    {
      title: 'Brand',
      dataIndex: 'brands',
      key: 'brands',
      width: 200,
      render: (brands) => {
        return (
          <Row align={'middle'} wrap={false} justify={'space-between'}>
            {!!brands?.length && (
              <Link to={`${ADMIN_ROUTES.brand.view}/${brands[0].id}/management`} target={'_blank'}>
                <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                  {brands[0].name}
                </Text>
              </Link>
            )}
            {brands?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={() => onBrandDrawerExpand(brands)}
              >
                <Text underline type={'secondary'}>
                  +{brands?.length - 1}
                </Text>
              </Button>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 130,
      ellipsis: true,
      render: (value) => {
        return (
          <Link to={`${ADMIN_ROUTES.suppliers.view}/${value?.id}/general`} target={'_blank'}>
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {value?.name}
            </Text>
          </Link>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <PurchaseOrderStatusBadge type={status} />,
    },
    {
      title: 'Delivery Deadline',
      dataIndex: 'deliveryDateFrom',
      key: 'deliveryDeadlineFrom',
      minWidth: 170,
      sorter: true,
      render: (_, record) => {
        const { deliveryDeadlineFrom, deliveryDeadlineTo } = record;
        if (!deliveryDeadlineFrom && !deliveryDeadlineTo) {
          return null;
        }
        const formattedDeliveryDateFrom = deliveryDeadlineFrom
          ? moment.utc(deliveryDeadlineFrom).format('DD.MM.YYYY')
          : null;
        const formattedDeliveryDateTo = deliveryDeadlineTo ? moment.utc(deliveryDeadlineTo).format('DD.MM.YYYY') : null;
        return formattedDeliveryDateFrom && formattedDeliveryDateTo
          ? `${formattedDeliveryDateFrom}-${formattedDeliveryDateTo}`
          : `${formattedDeliveryDateFrom || formattedDeliveryDateTo}`;
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'postSalesManager',
      key: 'postSalesManager',
      minWidth: 100,
      render: (value, record) => (
        <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 200 }}>
          {value?.firstName} {value?.lastName}{' '}
          {record.postSalesManagerAssistant
            ? `,${record.postSalesManagerAssistant.firstName} ${record.postSalesManagerAssistant.lastName}`
            : ''}
        </Text>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      minWidth: 120,
      render: (value) => value?.day,
      sorter: true,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      minWidth: 120,
      render: (value) => value?.day,
      sorter: true,
    },
  ];

  if (hasAccess(PERMISSIONS.purchaseOrders.purchaseOrder.read)) {
    columns.push({
      fixed: 'right',
      width: 68,
      key: 'actions',
      render: (value) => {
        return (
          <Row className={'gap-8'} wrap={false} align={'middle'} justify={'center'}>
            <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.read}>
              <Button
                onClick={() => routeChange(value.id)}
                style={{ padding: 0, width: 16, height: 16 }}
                type={'text'}
                size={'small'}
                icon={<ViewIcon />}
              />
            </HasAccess>
          </Row>
        );
      },
    });
  }

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Purchase Orders'} />
            <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
              <Button type={'primary'} onClick={onAddNew} icon={<PlusIcon color={'#FFFFFF'} />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <Table
                tableLayout={'auto'}
                bordered
                scroll={{
                  x: true,
                }}
                rowKey="id"
                size={'small'}
                dataSource={data}
                columns={columns}
                loading={{
                  indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                  spinning: isLoading,
                }}
                locale={
                  isLoading
                    ? { emptyText: null } // Hide "No Data" text while loading
                    : {} // Use default AntD "No Data" icon when empty
                }
                onChange={handleTableChange}
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} items`,
                  size: 'default',
                  current: pagination.page,
                  pageSize: pagination.size,
                  total: total,
                }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <BrandsDrawer onExpand={onBrandDrawerExpand} drawerState={brandDrawerState} data={brandsData} />
    </Col>
  );
};
